/**
 * billing.helpers — pure helpers for catalogue-driven payment methods.
 *
 * The v2 instruction-options response (`POST …/sessions/{id}/instructions`,
 * ISSUE-1976) carries a BILLING flavor listing the payment options the BE will
 * actually accept for this session. Before A1.2 `PaymentMethodSection` rendered
 * a hardcoded three, so a client could pick a method the BE rejects at place
 * order — and an admin (whose mimetype strips direct-payment options) was shown
 * card/SEPA that can never work.
 *
 * Mirrors `steps/instructions/meeting.helpers.ts`: pure (no React / store / API),
 * so the ladder is unit-testable and the section stays presentational.
 *
 * ⚠️ The FE `PaymentMethod` union stays 3-valued. Widening it would
 * ripple into `mapPaymentMethod` → `OrderPaymentMethodCode` → drafts → Summary,
 * so unmappable kinds (INVOICE_BY_POST, PURCHASE_ORDER, PERIODICAL_INVOICE,
 * FAILED_SEPA_DIRECT_DEBIT, …) are skipped with a single warning.
 *
 * ⚠️ R-3 CLOSED (ledger #14) — BE confirmed `CARD_PAYMENT` and `STRIPE_PAYMENT`
 * are DISTINCT billing-catalogue kinds (direct card vs Stripe-processed
 * checkout), not aliases: the org's actually-catalogued kind is validated at
 * place order and a mismatched kind is REJECTED, never coerced. So the UI →
 * wire direction (`mapPaymentMethod` in `store/serializePlacement.ts`) can no
 * longer hardcode `STRIPE_PAYMENT` for `'card'` — it echoes back whichever
 * kind {@link resolveCardPaymentKind} resolved from THIS session's BILLING
 * options.
 */

import type { PaymentMethod, ResolvedInstructionOption } from 'models/purchaseFlowRevamp'
import type { InvoiceOption } from '../../analytics/events'
import { ProductKind } from 'constants/product'
import { logger } from 'utils/logging'

/** Loading/ready/error status of the instruction options (mirrors the hook). */
type CatalogueStatus = 'idle' | 'loading' | 'ready' | 'error'

/** The three methods the FE can serialize — the fallback when the BE list is
 *  unknown (loading / error / no BILLING flavor). Never block ordering. */
export const FALLBACK_PAYMENT_METHODS: ReadonlyArray<PaymentMethod> = ['card', 'sepa', 'invoice']

/** Always-available method: an invoice by e-mail needs no payment instrument,
 *  and it is what the admin-on-behalf flow resolves to. */
const INVOICE_ONLY: ReadonlyArray<PaymentMethod> = ['invoice']

/**
 * Wire `ProductKind` → FE `PaymentMethod`.
 *
 * `CARD_PAYMENT` and `STRIPE_PAYMENT` both collapse to `'card'` (Stripe is the
 * card processor) — {@link resolveAvailablePaymentMethods} dedupes them.
 */
export const PAYMENT_KIND_TO_METHOD: Partial<Record<ProductKind, PaymentMethod>> = {
  [ProductKind.CARD_PAYMENT]: 'card',
  [ProductKind.STRIPE_PAYMENT]: 'card',
  [ProductKind.SEPA_DIRECT_DEBIT]: 'sepa',
  [ProductKind.INVOICE_BY_EMAIL]: 'invoice',
}

/** Map one BILLING option onto the FE union; `undefined` when unmappable. */
export const paymentMethodForOption = (option: ResolvedInstructionOption): PaymentMethod | undefined =>
  PAYMENT_KIND_TO_METHOD[option.kind]

/**
 * Resolve which payment methods the section should offer.
 *
 * Availability ladder (`billingOptions` = the BILLING flavor's options; EMPTY
 * means the response carried no BILLING flavor at all — `bucketInstructionOptions`
 * always returns an array):
 *
 *   1. `idle` / `loading` / `error` → the hardcoded three. We can't know what
 *      the BE offers and blocking checkout is worse than an over-offer (mirrors
 *      DF-I9's error branch in `meeting.helpers.ts`).
 *   2. `ready` + BILLING options + ≥1 mappable kind → exactly those, in BE order,
 *      deduped.
 *   3. `ready` + BILLING options + 0 mappable kinds → invoice only, warn. This is
 *      the expected ADMIN case (the admin mimetype strips direct-payment
 *      options): showing card/SEPA there invites a place-order rejection.
 *   4. `ready` + NO BILLING options → the hardcoded three, warn. Can't know ⇒
 *      don't block.
 */
export const resolveAvailablePaymentMethods = (
  billingOptions: ReadonlyArray<ResolvedInstructionOption> | undefined,
  status: CatalogueStatus
): ReadonlyArray<PaymentMethod> => {
  if (status !== 'ready') return FALLBACK_PAYMENT_METHODS

  if (!billingOptions || billingOptions.length === 0) {
    logger.warn('pf-revamp billing: instruction options carried no BILLING flavor, offering all methods', undefined, {
      context: { component: 'billing.helpers', action: 'resolveAvailablePaymentMethods' },
    })
    return FALLBACK_PAYMENT_METHODS
  }

  const methods: PaymentMethod[] = []
  const skipped: string[] = []
  for (const option of billingOptions) {
    const method = paymentMethodForOption(option)
    if (!method) {
      skipped.push(String(option.kind))
      continue
    }
    if (!methods.includes(method)) methods.push(method)
  }

  if (skipped.length > 0) {
    logger.warn('pf-revamp billing: skipping unmappable payment kinds', undefined, {
      context: {
        component: 'billing.helpers',
        action: 'resolveAvailablePaymentMethods',
        metadata: { kinds: skipped.join(',') },
      },
    })
  }

  if (methods.length === 0) {
    logger.warn('pf-revamp billing: no mappable payment method offered, falling back to invoice only', undefined, {
      context: { component: 'billing.helpers', action: 'resolveAvailablePaymentMethods' },
    })
    return INVOICE_ONLY
  }

  return methods
}

/**
 * The BE-preferred method (`isDefault`) as a RENDER-TIME effective value.
 *
 * MODULE-CONTRACT §11.1 (BINDING): this is deliberately NOT seeded into
 * `billingSlice.paymentMethod`. `paymentMethod` is persisted (it is in
 * `serializeDraft` and in the autosave fingerprint), so a mount-time write would
 * dirty every draft on every Billing mount/resume — the F-B1 back-nav-wipe
 * class. The page renders `paymentMethod ?? resolveDefaultPaymentMethod(...)`
 * and only a user click calls `setPaymentMethod`, so a resumed draft's stored
 * pick always wins (`??` precedence).
 *
 * Returns `undefined` unless the options are `ready` — an "unknown yet" default
 * must not preselect anything.
 */
export const resolveDefaultPaymentMethod = (
  billingOptions: ReadonlyArray<ResolvedInstructionOption> | undefined,
  status: CatalogueStatus
): PaymentMethod | undefined => {
  if (status !== 'ready' || !billingOptions) return undefined
  for (const option of billingOptions) {
    if (!option.isDefault) continue
    const method = paymentMethodForOption(option)
    if (method) return method
  }
  return undefined
}

/**
 * R-3 CLOSED (ledger #14) — resolve the ACTUAL wire kind (`CARD_PAYMENT` or
 * `STRIPE_PAYMENT`) behind the FE `'card'` bucket, so the serializer can echo
 * it back instead of assuming Stripe. An org is catalogued under exactly one
 * of the two (they collapse to the same FE method — {@link PAYMENT_KIND_TO_METHOD}),
 * so the first BILLING option that maps to `'card'` IS that org's kind.
 *
 * `undefined` when the options aren't `ready` yet or the org offers no
 * card-family option at all — the caller (`mapPaymentMethod`) falls back to
 * `STRIPE_PAYMENT`, the same "can't know ⇒ don't block" posture as the rest
 * of this ladder.
 */
export const resolveCardPaymentKind = (
  billingOptions: ReadonlyArray<ResolvedInstructionOption> | undefined,
  status: CatalogueStatus
): ProductKind | undefined => {
  if (status !== 'ready' || !billingOptions) return undefined
  const cardOption = billingOptions.find((option) => paymentMethodForOption(option) === 'card')
  return cardOption?.kind
}

/* -------------------------------------------------------------------------- *
 *  Payment-method parity with legacy's BillingMethodInstructionCard
 * -------------------------------------------------------------------------- */

/** Per-method view data the section needs beyond the bare union. */
export interface PaymentMethodOffer {
  method: PaymentMethod
  /** The BILLING option behind it, for the price label. */
  option?: ResolvedInstructionOption
  /** Fee this method adds, 0 when free or unpriced. */
  amount: number
  /** `false` → render locked, with the subscribe upsell instead of a choice. */
  allowed: boolean
}

/**
 * Legacy's invoice rule (`BillingMethodInstructionCard.isMethodAllowed`):
 * everything except invoice is always allowed; invoice needs an admin, or an
 * org permitted to pay by invoice, or a PRICED invoice option — some orgs buy
 * the paid invoice product instead of being granted the free one.
 *
 * Anything else is allowed: the BILLING options are already org-filtered by the
 * BE, so a method that reached us is one the org may use.
 */
export const isPaymentMethodAllowed = (
  method: PaymentMethod,
  option: ResolvedInstructionOption | undefined,
  { isAdmin, invoiceAllowed }: { isAdmin: boolean, invoiceAllowed: boolean }
): boolean => {
  if (method !== 'invoice') return true
  if (isAdmin) return true
  if (invoiceAllowed) return true
  return (option?.amount ?? 0) > 0
}

/**
 * The invoice card as the billing screen shows it, for `invoice_option` on its entry: free and selectable, priced,
 * shown free but locked, or not there (a free order renders no payment cards). Null when the options failed, and
 * undefined while they are still loading, so the entry waits for them.
 */
export const resolveInvoiceOption = ({ isSettled, hasError, isFreeCart, offers }: {
  isSettled: boolean
  hasError: boolean
  isFreeCart: boolean
  offers: ReadonlyArray<Pick<PaymentMethodOffer, 'method' | 'amount' | 'allowed'>>
}): InvoiceOption | null | undefined => {
  if (!isSettled) return undefined
  if (hasError) return null
  const invoice = isFreeCart ? undefined : offers.find((offer) => offer.method === 'invoice')
  if (!invoice) return 'not_offered'
  if (invoice.amount > 0) return 'paid'
  return invoice.allowed ? 'waived' : 'locked'
}

/**
 * Joins the offered methods with their BILLING option and permission, so the
 * section can render price, badge and lock state without re-deriving any of it.
 */
export const resolvePaymentMethodOffers = (
  methods: ReadonlyArray<PaymentMethod>,
  billingOptions: ReadonlyArray<ResolvedInstructionOption> | undefined,
  permissions: { isAdmin: boolean, invoiceAllowed: boolean }
): PaymentMethodOffer[] =>
  methods.map((method) => {
    const option = billingOptions?.find((candidate) => paymentMethodForOption(candidate) === method)
    return {
      method,
      option,
      amount: option?.amount ?? 0,
      allowed: isPaymentMethodAllowed(method, option, permissions),
    }
  })

/**
 * Legacy's full preselect ladder (`PurchaseFlowOrderMeta.updatePaymentMethod`),
 * which v2 had reduced to the BE's `isDefault` alone:
 *
 *   1. the CLIENT's own saved preferred method, when it is offered and permitted
 *      — legacy checked this before anything else for client roles;
 *   2. otherwise the BE's `isDefault`, when permitted;
 *   3. otherwise nothing, so the step asks.
 *
 * Reading only `isDefault` was visibly wrong: a client whose preferred method is
 * invoice saw the invoice card badged "Default" while SEPA was the selected one.
 */
export const resolvePreselectedMethod = (
  offers: ReadonlyArray<PaymentMethodOffer>,
  beDefault: PaymentMethod | undefined,
  preferred: PaymentMethod | undefined
): PaymentMethod | undefined => {
  const permitted = (method: PaymentMethod | undefined) =>
    !!method && offers.some((offer) => offer.method === method && offer.allowed)

  if (permitted(preferred)) return preferred
  if (permitted(beDefault)) return beDefault
  return undefined
}

/** Maps a stored `preferredPaymentMethod` product kind onto the FE union. */
export const paymentMethodForPreferredKind = (kind: string | undefined | null): PaymentMethod | undefined =>
  kind ? PAYMENT_KIND_TO_METHOD[kind as ProductKind] : undefined
